/**
 * RewampRoutes — the application route tree (rewamp). This replaces src/Route.js as the
 * single routing entry point wired into App.js. src/Route.js is kept only for reference.
 *
 * HOW THIS FILE IS ORGANISED
 *   1. Lazy imports — one per page, grouped by layout.
 *   2. <PwaShellLayout> wraps 3–5: in the installed app it owns the PWA header + bottom
 *      bar on every signed-in route; in a browser tab it renders the Outlet only.
 *   3. <RewampLayout> block  — pages on the new Bihar Krishi theme.
 *   4. <DefaultLayout> block — legacy-themed pages (in a browser tab also wearing
 *      RewampAppBar + RewampFooter as chrome; only the Outlet keeps the
 *      legacy theme, so legacy page bodies render unchanged).
 *   5. Catch-all redirect.
 *
 * RULES (keep these true on every edit)
 *   • Access control is declared via ROLE_GROUPS (./roleGroups.js) — never inline a role
 *     array here. Pick an existing group or add a new named one there.
 *   • <ProtectedRoute access_roles={...}> gates its nested <Route> children. Add
 *     publicAccess so a route is reachable before login and gated only after login.
 *   • Top-level paths are built with cp('segment') → `${contextPath}/segment`. Child paths
 *     under a parent <Route> stay relative (no cp, no leading slash).
 *   • wrap()      → standard Suspense skeleton.  mainWrap() → full-height landing skeleton.
 *
 * 📖 Route map + RBAC reference: .agent/docs/rewamp-routes.md   (skill: /rewamp-routes)
 *    Update that doc whenever you add, move, or re-gate a route.
 */
import React, { Suspense, lazy, useContext, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import PropTypes from "prop-types";
import { Skeleton, ThemeProvider } from "@mui/material";

import ProtectedRoute from "../../components/ProtectedRouteComponent";
import { useDisplayMode } from "../context/DisplayModeContext";
import { ROLE_GROUPS } from "./roleGroups";
import { rewampDashboardAccess } from "../components/RewampUtils";
import DefaultLayout from "../../layouts/DefaultLayout";
import RewampLayout from "../layouts/RewampLayout";
import PwaShellLayout from "../layouts/PwaShellLayout";
import { PWA_ENTRY, chromeFor } from "../layouts/pwaConfig";
import FarmerContextRoute from "./FarmerContextRoute";
import SelfIdentityRoute from "./SelfIdentityRoute";
import { GRM_STREAMS } from "../../AppModules/HelpSupport/config/grmStreams";
import { grmCrumbs } from "../../AppModules/HelpSupport/utils/grmBreadcrumbs";
import RewampErrorBoundary from "../components/RewampErrorBoundary";
import RequireSchemeContext from "./RequireSchemeContext";
import generateTheme from "../../theme";
import generateRewampTheme from "../theme";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";
import { getUserRoles } from "../../components/Utils";
import PwaAccessGuard, { StandaloneEntry } from "./PwaAccessGuard";
import LaunchReady from "../components/pwa/LaunchReady";
import { loggedOutPath } from "./pwaSession";
import { FARMER_VERIFY_HEADER } from "../localisation";

// ── Rewamp-theme pages ──────────────────────────────────────────────────────
const RewampMainLandingPage = lazy(() => import( /* webpackChunkName: "RewampMainLandingPage"*/ "../pages/MainLandingPage/RewampMainLandingPage"));
const PwaHomePage = lazy(() => import( /* webpackChunkName: "PwaHomePage"*/ "../pages/MainLandingPage/PwaHomePage"));
const RewampLoginPage = lazy(() => import( /* webpackChunkName: "RewampLoginPage" */ "../pages/LoginSignupOtp/RewampLoginPage"));
const PwaLanguagePickerPage = lazy(() => import( /* webpackChunkName: "PwaLanguagePickerPage" */ "../pages/PwaLanguagePicker/PwaLanguagePickerPage"));
const RewampResetPasswordPage = lazy(() => import( /* webpackChunkName: "RewampResetPasswordPage" */ "../pages/LoginSignupOtp/RewampResetPasswordPage"));
// Assets (DFS-3553) — re-parented from DefaultLayout to RewampLayout.
const AssetsPage = lazy(() => import( /* webpackChunkName: "AssetsPage" */ "../../pages/Assets/AssetsPage"));
const AssetsDetailPage = lazy(() => import( /* webpackChunkName: "AssetsDetailPage" */ "../../pages/Assets/AssetsDetailPage"));
// Agri Input Marketplace ("Bazar") — new module, built directly under
// RewampLayout (Assets' pattern), no DefaultLayout/hortiWrap theme-rewrap.
const AgriInputListingPage = lazy(() => import( /* webpackChunkName: "AgriInputListingPage" */ "../../AppModules/AgriInput/pages/AgriInputListingPage"));
const GenericProductPage = lazy(() => import( /* webpackChunkName: "GenericProductPage" */ "../../AppModules/AgriInput/pages/GenericProductPage"));
const AllBrandsPage = lazy(() => import( /* webpackChunkName: "AllBrandsPage" */ "../../AppModules/AgriInput/pages/AllBrandsPage"));
const ProductDetailsPage = lazy(() => import( /* webpackChunkName: "ProductDetailsPage" */ "../../AppModules/AgriInput/pages/ProductDetailsPage"));
const RewampRecoverDbtIdPage = lazy(() => import( /* webpackChunkName: "RewampRecoverDbtIdPage" */ "../pages/RecoverDbtId/RewampRecoverDbtIdPage"));

// ── Default-theme pages ─────────────────────────────────────────────────────
const MandiPricePage = lazy(() => import( /* webpackChunkName: "MandiPricePage" */ "../../pages/MandiPrice/MandiPricePage"));
const CarousalManagement = lazy(() => import( /* webpackChunkName: "CarousalManagement" */ "../../pages/Announcements/CarousalManagement"));
const PgrRouter = lazy(() => import( /* webpackChunkName: "PgrRouter" */ "../../AppModules/PGR/PgrRouter"));
const EditProfile = lazy(() => import( /* webpackChunkName: "EditProfile" */ "../../pages/LoginSignupOtp/EditProfile"));
const PwaProfilePage = lazy(() => import( /* webpackChunkName: "PwaProfilePage" */ "../pages/PwaProfile/PwaProfilePage"));
const MandiPriceDetailsPage = lazy(() => import( /* webpackChunkName: "MandiPriceDetailsPage" */ "../../pages/MandiPrice/MandiPriceDetailsPage"));
const SchemesTabLayout = lazy(() => import( /* webpackChunkName: "SchemesTabLayout" */ "../../AppModules/Schemes/SchemesTabLayout"));
const SchemesAllTab = lazy(() => import( /* webpackChunkName: "SchemesAllTab" */ "../../AppModules/Schemes/SchemesAllTab"));
// const SchemesRecommendedTab = lazy(() => import( /* webpackChunkName: "SchemesRecommendedTab" */ "../../AppModules/Schemes/SchemesRecommendedTab")); // Recommended Schemes tab hidden
const SchemesAppliedTab = lazy(() => import( /* webpackChunkName: "SchemesAppliedTab" */ "../../AppModules/Schemes/SchemesAppliedTab"));
const SchemeInfo = lazy(() => import( /* webpackChunkName: "SchemeInfo" */ "../../AppModules/Schemes/SchemeInfo"));
const SchemePageForm = lazy(() => import( /* webpackChunkName: "SchemePageForm" */ "../../AppModules/Schemes/SchemePageForm"));
const TechSupportModule = lazy(() => import( /* webpackChunkName: "TechSupportModule" */ "../../AppModules/GRM9/TechSupportModule"));
const FarmerRegistrationForm = lazy(() => import( /* webpackChunkName: "FarmerRegistrationForm" */ "../../AppModules/Agent/FarmerRegistrationForm"));
const AgentHistory = lazy(() => import( /* webpackChunkName: "AgentHistory" */ "../../AppModules/Agent/AgentHistory"));
const TrackService = lazy(() => import( /* webpackChunkName: "TrackService" */ "../../AppModules/Agent/GRM/TrackService"));
const ComplaintDetails = lazy(() => import( /* webpackChunkName: "ComplaintDetails" */ "../../AppModules/PGR/ComplaintDetails"));
const KeyContactsPage = lazy(() => import( /* webpackChunkName: "KeyContactsPage" */ "../../pages/DescriptionPages/KeyContactsPage"));
const AboutPage = lazy(() => import( /* webpackChunkName: "AboutPage" */ "../../pages/DescriptionPages/AboutPage"));
const HortiListingPage = lazy(() => import( /* webpackChunkName: "HortiListingPage" */ "../../AppModules/Horti/pages/HortiListingPage"));
const HortiProviderDetailPage = lazy(() => import( /* webpackChunkName: "HortiProviderDetailPage" */ "../../AppModules/Horti/pages/HortiProviderDetailPage"));
const HortiEnquiryHistoryPage = lazy(() => import( /* webpackChunkName: "HortiEnquiryHistoryPage" */ "../../AppModules/Horti/pages/HortiEnquiryHistoryPage"));
const AdvisoriesLandingPage = lazy(() => import( /* webpackChunkName: "AdvisoriesLandingPage" */ "../pages/Advisories/AdvisoriesLandingPage"));
const AgrometAdvisoryDetailPage = lazy(() => import( /* webpackChunkName: "AgrometAdvisoryDetailPage" */ "../pages/Advisories/AgrometAdvisoryDetailPage"));
const PrivacyPolicyPage = lazy(() => import( /* webpackChunkName: "PrivacyPolicyPage" */ "../../pages/PrivacyPolicy/PrivacyPolicyPage"));
const ScreenReaderPage = lazy(() => import( /* webpackChunkName: "ScreenReaderPage" */ "../../pages/ScreenReader/ScreenReaderPage"));
const SiteMap = lazy(() => import( /* webpackChunkName: "SiteMap" */ "../../pages/SiteMap/SiteMap"));
const HelpSupportLandingPage = lazy(() => import( /* webpackChunkName: "HelpSupportLandingPage" */ "../../AppModules/HelpSupport/pages/HelpSupportLandingPage"));
const HelpDirectoryPage = lazy(() => import( /* webpackChunkName: "HelpDirectoryPage" */ "../../AppModules/HelpSupport/pages/HelpDirectoryPage"));
const FaqPage = lazy(() => import( /* webpackChunkName: "FaqPage" */ "../../AppModules/HelpSupport/pages/FaqPage"));
const FaqDetailPage = lazy(() => import( /* webpackChunkName: "FaqDetailPage" */ "../../AppModules/HelpSupport/pages/FaqDetailPage"));
const GrmListingPage = lazy(() => import( /* webpackChunkName: "GrmListingPage" */ "../../AppModules/HelpSupport/pages/GrmListingPage"));
const GrmRaisePage = lazy(() => import( /* webpackChunkName: "GrmRaisePage" */ "../../AppModules/HelpSupport/pages/GrmRaisePage"));
const GrmTicketPage = lazy(() => import( /* webpackChunkName: "GrmTicketPage" */ "../../AppModules/HelpSupport/pages/GrmTicketPage"));
const AccessibilityStatementPage = lazy(() => import( /* webpackChunkName: "AccessibilityStatementPage" */ "../../pages/AccessibilityStatement/AccessibilityStatementPage"));
const DataCleanupModule = lazy(() => import( /* webpackChunkName: "DataCleanupModule" */ "../../AppModules/DataCleanup/DataCleanupModule"));
// Admin detail keeps legacy chrome: DefaultLayout has no rewamp tokens, so the rewamp AssetsDetailPage would throw here.
const AssetsDetailCleanupPage = lazy(() => import( /* webpackChunkName: "AssetsDetailCleanupPage" */ "../../pages/Assets/AssetsDetailCleanupPage"));
const FarmerPassbook = lazy(() => import( /* webpackChunkName: "FarmerPassbook" */ "../../pages/FarmerPassbook"));
const DSSModule = lazy(() => import( /* webpackChunkName: "DSSModule" */ "../../AppModules/DSS/DSSModule"));
const SoilLabPage = lazy(() => import( /* webpackChunkName: "SoilLabPage" */ "../../pages/SoilHealth/SoilLabPage"));
const SoilCardPage = lazy(() => import( /* webpackChunkName: "SoilCardPage" */ "../../pages/SoilHealth/SoilCardPage"));
const SoilHealthDetailsPage = lazy(() => import( /* webpackChunkName: "SoilHealthDetailsPage" */ "../../pages/SoilHealth/SoilHealthDetailsPage"));
const ApplicationDetails = lazy(() => import( /* webpackChunkName: "ApplicationDetails" */ "../../AppModules/Schemes/ApplicationDetails"));
const RegistrationForm = lazy(() => import( /* webpackChunkName: "RegistrationForm" */ "../../AppModules/Farmer/RegistrationForm"));
// Legacy /home landing dispatcher (AgentHomePage / LandingPage by role) — shown
// to non-rewamp roles via HomeLanding below.
const Homepage = lazy(() => import( /* webpackChunkName: "Homepage" */ "../../pages/Homepage"));

/** Build a context-path-prefixed top-level route path: cp('mandi') → `${contextPath}/mandi`. */
const cp = (segment) => `${window.contextPath}/${segment}`;

export const ApplicationRoutes = ({ isMobile, initData }) => {
  const { standalone } = useDisplayMode();
  const fallbackFor = (sx) => (standalone ? null : <Skeleton sx={sx} />);
  const wrap = (node) => (
    <Suspense fallback={fallbackFor()}>
      <LaunchReady />
      {node}
    </Suspense>
  );
  const mainWrap = (node) => (
    <Suspense fallback={fallbackFor({ width: "100%", height: "80vh" })}>
      <LaunchReady />
      {node}
    </Suspense>
  );

  // Horti pages are authored against the rewamp design system (rewampTheme.js
  // tokens + variants — infoCard / categoryPill / sectionTitle / status.* /
  // primary.chipActive — none of which exist in the legacy theme), but they live
  // in the DefaultLayout block whose <Outlet> stays on the legacy App.js theme.
  // Re-apply the rewamp theme over just the horti bodies (same factory + memo
  // convention as RewampLayout/DefaultLayout, and the same "re-theme one body"
  // move HomeLanding uses for legacy roles). ThemeContext (ourTheme) flows in via
  // AppLayoutContext, so horti follows light/dark exactly like the rewamp shell.
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const rewampTheme = useMemo(() => generateRewampTheme(fontSize, ourTheme), [fontSize, ourTheme]);
  const hortiWrap = (node) => (
    <Suspense fallback={fallbackFor()}>
      <LaunchReady />
      <ThemeProvider theme={rewampTheme}>{node}</ThemeProvider>
    </Suspense>
  );

  return (
    <RewampErrorBoundary>
    <Routes>
      <Route element={<PwaAccessGuard />}>
      <Route element={<PwaShellLayout />}>
        {/* ════════════════════════════════════════════════════════════════════
            REWAMP LAYOUT — Bihar Krishi Figma theme
            ════════════════════════════════════════════════════════════════════ */}
        <Route element={<RewampLayout chrome={chromeFor(standalone)} />}>
          {/* Public */}
          <Route path={`${window.contextPath}`} element={standalone ? <StandaloneEntry /> : mainWrap(<RewampMainLandingPage isMobile={isMobile} />)} />
          <Route path={cp("login")} element={standalone ? <StandaloneEntry>{mainWrap(<RewampLoginPage />)}</StandaloneEntry> : mainWrap(<RewampLoginPage />)} />
          {standalone && <Route path={cp(PWA_ENTRY.languagePath)} element={mainWrap(<PwaLanguagePickerPage />)} />}
          <Route path={cp("change-password")} element={mainWrap(<RewampResetPasswordPage />)} />
          <Route path={cp("recover-dbt-id")} element={mainWrap(<RewampRecoverDbtIdPage />)} />
          {/* Every advisories section is public, agromet included. */}
          <Route path={cp("advisories")} element={wrap(<AdvisoriesLandingPage />)} />
          <Route path={cp("advisories/agromet/:category/:itemId")} element={wrap(<AgrometAdvisoryDetailPage />)} />
          <Route path={cp("faq")} element={wrap(<FaqPage isMobile={isMobile} />)} />
          <Route path={cp("faq/faq-details")} element={wrap(<FaqDetailPage isMobile={isMobile} />)} />

          {/* Common authenticated area — agent / employee / KCC / state-admin / citizen */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.COMMON_AUTHENTICATED} />}>
            <Route
              path={cp("edit-profile")}
              element={wrap(standalone ? <PwaProfilePage isMobile={isMobile} /> : <EditProfile isMobile={isMobile} />)}
            />
            {/* Role-dispatched landing — see HomeLanding. The URL stays /home for
                everyone; only the rendered page (and its body theme) differs. */}
            <Route path={cp("home")} element={wrap(<HomeLanding isMobile={isMobile} initData={initData} />)} />
          </Route>

          {/* ── Schemes LIST tabs (rewamp body) ──────────────────────────────
              Public before login · agent / citizen after. The scheme
              details/apply/applied-details routes now sit alongside these under
              RewampLayout too (block below) — the whole scheme flow shares the
              rewamp theme + chrome. Two sibling <Route path={cp("schemes")}>
              resolve fine — RR v6 ranks the disjoint leaf paths. */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            <Route path={cp("schemes")}>
              {/* Tab pair shares chrome via SchemesTabLayout (see tab-based-rbac-routes.md) */}
              <Route element={wrap(<SchemesTabLayout isMobile={isMobile} />)}>
                <Route index element={wrap(<SchemesAllTab />)} />
                {/* Recommended Schemes tab hidden (DFS-3547 follow-up) — commented
                    out alongside the landing card in categorySectionData.js.
                    Restore both together to bring the flow back. */}
                {/* <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
                  <Route element={<FarmerContextRoute headerText="Recommended_Schemes" />}>
                    <Route path="recommended" element={wrap(<SchemesRecommendedTab />)} />
                  </Route>
                </Route> */}
                {/* Applied Schemes tab is citizen-only */}
                <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.CITIZEN_ONLY} />}>
                  <Route path="applied" element={wrap(<SchemesAppliedTab />)} />
                </Route>
              </Route>
            </Route>
          </Route>

          {/* ── Scheme details / apply / application-details ───────────────────
              Re-united with the list tabs under RewampLayout now that the detail
              body is rewamped (DFS-3599); apply (DFS-3600) and applied-details
              (DFS-3601) ride along on the rewamp theme + chrome. Two sibling
              <Route path={cp("schemes")}> resolve fine — RR v6 ranks the disjoint
              leaf paths. Public before login · agent / citizen after. */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            <Route path={cp("schemes")}>
              {/* Sibling pages keep their own chrome — NOT inside the tab layout */}
              <Route path="details">
                <Route index element={wrap(<SchemeInfo />)} />
                {/* Applying requires agent or citizen — identity
                    (SCHEME_APPLY_PROFILE_GET) comes from FarmerContextRoute so
                    SchemePageForm itself doesn't need the inline DBT/OTP form
                    anymore (PR 5). Transactional OTP at submit time is still
                    AGENT-only and lives inside useSchemeSubmit. */}
                <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
                  <Route element={<RequireSchemeContext />}>
                    <Route element={<FarmerContextRoute categoryType="SCHEME_APPLY_PROFILE_GET" />}>
                      <Route path="apply" element={wrap(<SchemePageForm />)} />
                    </Route>
                  </Route>
                </Route>
              </Route>
              {/* Application details are citizen-only. Path is nested under
                  `applied/` because AppliedSchemesGrid navigates here with a
                  relative `navigate("application-details")` from the Applied
                  Schemes tab (/schemes/applied). Kept a sibling of the tab
                  layout so it renders with its own chrome, not the tabs. */}
              <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.CITIZEN_ONLY} />}>
                <Route path="applied/application-details" element={wrap(<ApplicationDetails />)} />
              </Route>
            </Route>
          </Route>

          {/* ── Assets (DFS-3553) · public before login · agent / KCC / citizen after login ── */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_KCC_CITIZEN} />}>
            <Route path={cp("assets-section")}>
              <Route index element={wrap(<AssetsPage isMobile={isMobile} />)} />
              <Route path="assets-details">
                <Route index element={wrap(<AssetsDetailPage isMobile={isMobile} />)} />
              </Route>
            </Route>
          </Route>

          {/* ── Agri Input Marketplace ("Bazar") · public before login · agent / KCC / citizen after login ──
              RBAC matches its Bazaar-tab siblings (Mandi, Assets), not Horti's CITIZEN_ONLY —
              this is a public catalog browse; only the favourite-toggle write needs a login
              (gated inline in AgriInputListingPage). See Pages/ListPage/INTEGRATION-PLAN.md FRAME. */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_KCC_CITIZEN} />}>
            <Route path={cp("agri-market")}>
              <Route index element={wrap(<AgriInputListingPage isMobile={isMobile} />)} />
              {/* Category-Selected / Brand-Selected results (Pages/GenericProductPage.js/
                  Layout.md) — one page for both `?categoryId=`/`?brandId=` entry points,
                  already wired forward-compatibly by AgriInputListingPage.js's tile clicks. */}
              <Route path="brands" element={wrap(<AllBrandsPage isMobile={isMobile} />)} />
              <Route path="category" element={wrap(<GenericProductPage isMobile={isMobile} />)} />
              <Route path="brand" element={wrap(<GenericProductPage isMobile={isMobile} />)} />
              {/* Section "View all" expansions — same page, source-driven title + data feed. */}
              <Route path="trending" element={wrap(<GenericProductPage source="trending" isMobile={isMobile} />)} />
              <Route path="liked" element={wrap(<GenericProductPage source="liked" isMobile={isMobile} />)} />
              <Route path="product-details" element={wrap(<ProductDetailsPage isMobile={isMobile} />)} />
            </Route>
          </Route>

          {/* ── Help & Support landing · public before login · agent / KCC / citizen after login ── */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_KCC_CITIZEN} />}>
            <Route path={cp("help")}>
              <Route index element={wrap(<HelpSupportLandingPage isMobile={isMobile} />)} />
              <Route path="help-details" element={wrap(<HelpDirectoryPage isMobile={isMobile} />)} />
            </Route>
          </Route>

          {/* ── Grievances · AGENT verifies the farmer once (PROFILE_GET); listing, details and raise share that identity ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            <Route element={<FarmerContextRoute />}>
              <Route path={cp("help/grievances")}>
                <Route index element={wrap(<GrmListingPage />)} />
                <Route path="new" element={wrap(<GrmRaisePage />)} />
                <Route path=":ticketId" element={wrap(<GrmTicketPage />)} />
              </Route>
            </Route>
          </Route>

          {/* ── Technical issues · same GRM pages on GRM9; AGENT picks Self (no verification) or Farmer (NON_DBT_PROFILE_GET) ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            <Route
              element={
                <SelfIdentityRoute
                  selfCrumb={grmCrumbs(GRM_STREAMS.techSupport).list}
                  categoryType="NON_DBT_PROFILE_GET"
                />
              }
            >
              <Route path={cp(GRM_STREAMS.techSupport.route)}>
                <Route index element={wrap(<GrmListingPage key="techSupport" stream={GRM_STREAMS.techSupport} />)} />
                <Route path="new" element={wrap(<GrmRaisePage key="techSupport" stream={GRM_STREAMS.techSupport} />)} />
                <Route path=":ticketId" element={wrap(<GrmTicketPage key="techSupport" stream={GRM_STREAMS.techSupport} />)} />
              </Route>
            </Route>
          </Route>
        </Route>

        {/* ════════════════════════════════════════════════════════════════════
            DEFAULT LAYOUT — legacy page bodies; in a browser tab wearing
            RewampAppBar + RewampFooter as chrome (chrome is wrapped in the
            rewamp theme inside DefaultLayout so its tokens resolve correctly).
            ════════════════════════════════════════════════════════════════════ */}
        <Route element={<DefaultLayout />}>
          {/* Guest tech-support pages retired: the revamp listing asks for login. */}
          {["report-track-issue", "report-issue", "track-issue"].map((page) => (
            <Route key={page} path={cp(`technical-support/${page}`)} element={<Navigate replace to={cp("help/tech-support")} />} />
          ))}
          {/* GRM support officers (TIRO) still open tickets here from /grm/inbox. */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.GRM_EMPLOYEE} />}>
            <Route path={cp("technical-support/*")} element={wrap(<TechSupportModule />)} />
          </Route>

          {/* ── Public, no role gating ── */}
          {/* Superseded by /advisories; kept so old links and bookmarks still land. */}
          <Route path={cp("weatherdata")} element={<Navigate to={cp("advisories")} replace />} />
          {/* Soil-testing lab directory was Tab 0 of /Soil_health (gated by
              the agent OTP) but is a public data lookup; promoted here. */}
          <Route path={cp("soil-lab")} element={wrap(<SoilLabPage isMobile={isMobile} />)} />
          <Route path={cp("key-contacts")} element={wrap(<KeyContactsPage isMobile={isMobile} />)} />
          <Route path={cp("screenreaderaccess")} element={wrap(<ScreenReaderPage isMobile={isMobile} />)} />
          <Route path={cp("about-section")} element={wrap(<AboutPage isMobile={isMobile} />)} />
          <Route path={cp("privacypolicy")} element={wrap(<PrivacyPolicyPage isMobile={isMobile} />)} />
          <Route path={cp("accessibility-statement")} element={wrap(<AccessibilityStatementPage isMobile={isMobile} />)} />
          <Route path={cp("sitemap")} element={wrap(<SiteMap />)} />

          {/* ── Public before login · agent / KCC / citizen after login ── */}
          <Route element={<ProtectedRoute publicAccess access_roles={ROLE_GROUPS.AGENT_KCC_CITIZEN} />}>
            <Route path={cp("mandi")}>
              <Route index element={wrap(<MandiPricePage isMobile={isMobile} />)} />
              <Route path="price-details" element={wrap(<MandiPriceDetailsPage isMobile={isMobile} />)} />
            </Route>
          </Route>

          {/* ── Horti Services · CITIZEN login required ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.CITIZEN_ONLY} />}>
            <Route path={cp("horti")}>
              <Route index element={hortiWrap(<HortiListingPage isMobile={isMobile} />)} />
              <Route path="provider-details" element={hortiWrap(<HortiProviderDetailPage isMobile={isMobile} />)} />
              <Route path={`enquiry-history`} element={hortiWrap(<HortiEnquiryHistoryPage isMobile={isMobile} />)} />
            </Route>
          </Route>


          {/* Back-compat: legacy agent GRM URLs → rewamp grievance pages (FarmerContextRoute gates both). */}
          <Route path={cp("grievance-redressal")} element={<Navigate replace to={`${window.contextPath}/help/grievances`} />} />
          <Route path={cp("grm-create")} element={<Navigate replace to={`${window.contextPath}/help/grievances/new`} />} />
          <Route path={`${cp("grm-create")}/*`} element={<Navigate replace to={`${window.contextPath}/help/grievances/new`} />} />

          {/* ── Farmer Passbook — both AGENT and CITIZEN ──
              AGENT folds the FARMER_PASSBOOK_GET OTP into the middleware
              (one OTP only). CITIZEN goes through
              /farmer-passbook/v1/_search keyed off their own userUuid —
              no OTP, no tokenId.
              See .agent/rewamp/cta/middleware-refactor-plan.md §4.2 +
              .agent/rewamp/cta/finishing/citizen-wiring-implementation-plan.md PR B. */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            {/* AGENT folds the FARMER_PASSBOOK_GET OTP into the middleware
                (one OTP instead of two consecutive ones). The resulting
                tokenId is exposed via useFarmer().resourceTokenId and
                usePassbook skips its inline OTP gate when it sees it.
                CITIZEN ignores categoryType — getFarmerprofile() is used
                for that branch regardless. */}
            <Route element={<FarmerContextRoute headerText={FARMER_VERIFY_HEADER.FARMER_PASSBOOK} categoryType="FARMER_PASSBOOK_GET" />}>
              <Route path={cp("farmer-passbook")} element={wrap(<FarmerPassbook isMobile={isMobile} />)} />
            </Route>
          </Route>

          {/* Back-compat: /agent-access (the old OTP entry page) and
              /agent-access/farmer-passbook deep links land on the new
              top-level /farmer-passbook route. */}
          <Route path={cp("agent-access")} element={<Navigate replace to={`${window.contextPath}/farmer-passbook`} />} />
          <Route path={`${cp("agent-access")}/*`} element={<Navigate replace to={`${window.contextPath}/farmer-passbook`} />} />

          {/* ── Soil Health Card — both AGENT and CITIZEN ──
              AGENT folds the SOIL_CARD_STATUS_GET OTP into the middleware
              (one OTP instead of two). useSoilCards pulls the bundled
              SoilTestAnalysisStatuses + tokenId from useFarmer().resourcePayload
              on mount and skips its inline OTP. CITIZEN auto-fetches
              /soil-card/v1/card-status keyed off their own dbtId. */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_CITIZEN} />}>
            <Route element={<FarmerContextRoute headerText={FARMER_VERIFY_HEADER.SOIL_CARD} categoryType="SOIL_CARD_STATUS_GET" />}>
              <Route path={cp("soil-card")}>
                <Route index element={wrap(<SoilCardPage isMobile={isMobile} />)} />
                <Route path="details" element={wrap(<SoilHealthDetailsPage isMobile={isMobile} />)} />
              </Route>
            </Route>
          </Route>

          {/* Back-compat: the old /farmer-details (OTP entry) and
              /Soil_health* deep links redirect onto the new /soil-card
              tree. The detail page inside /Soil_health used route state
              for testGrid + tokenId, so a bare redirect lands the user
              on the listing page — they re-enter via row click. */}
          <Route path={cp("farmer-details")} element={<Navigate replace to={`${window.contextPath}/soil-card`} />} />
          <Route path={cp("Soil_health")} element={<Navigate replace to={`${window.contextPath}/soil-card`} />} />
          <Route path={`${cp("Soil_health")}/*`} element={<Navigate replace to={`${window.contextPath}/soil-card`} />} />

          {/* ── Assisted-mode agent only ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.AGENT_ONLY} />}>
            <Route path={cp("registration")} element={wrap(<FarmerRegistrationForm />)} />
            <Route path={cp("track")}>
              <Route index element={wrap(<TrackService isMobile={isMobile} />)} />
              <Route path="complaintdetails/:complaintId" element={wrap(<ComplaintDetails />)} />
            </Route>
            <Route path={cp("history")} element={wrap(<AgentHistory isMobile={isMobile} />)} />
          </Route>

          {/* ── Announcement admin ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.ANNOUNCEMENT_ADMIN} />}>
            <Route path={cp("carousel-management")} element={wrap(<CarousalManagement />)} />
          </Route>

          {/* ── GRM / grievance employees ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.GRM_EMPLOYEE} />}>
            <Route path={cp("grm/*")} element={wrap(<PgrRouter />)} />
          </Route>

          {/* ── Decision-support dashboards ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.DASHBOARD_ADMIN} />}>
            <Route path={cp("dashboards")} element={wrap(<DSSModule initData={initData} />)} />
          </Route>

          {/* ── Data review / cleanup ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.DATA_REVIEW_ADMIN} />}>
            <Route path={cp("data-cleanup")}>
              <Route index element={wrap(<DataCleanupModule />)} />
              <Route path="assets-details" element={wrap(<AssetsDetailCleanupPage isMobile={isMobile} />)} />
            </Route>
          </Route>

          {/* ── Citizen only ── */}
          <Route element={<ProtectedRoute access_roles={ROLE_GROUPS.CITIZEN_ONLY} />}>
            <Route path={cp("farmer-registration")} element={wrap(<RegistrationForm isMobile={isMobile} />)} />
          </Route>
        </Route>

        {/* Catch-all — outside layouts since Navigate renders no UI */}
        <Route path="*" element={<FallbackRoute />} />
      </Route>
      </Route>
    </Routes>
    </RewampErrorBoundary>
  );
};

/**
 * HomeLanding — role dispatch for the shared `/home` URL (gated upstream by
 * COMMON_AUTHENTICATED, so only logged-in users reach it; public/logged-out are
 * bounced to `/` by ProtectedRoute).
 *
 *   AGENT / CITIZEN (ROLE_GROUPS.HOME_REWAMP_LANDING)
 *       → RewampMainLandingPage, rendered under the RewampLayout rewamp theme
 *         that already wraps this route.
 *   every other role (EMPLOYEE / KCC / STADMIN …)
 *       → the legacy Homepage dispatcher (KCC → AgentHomePage,
 *         employees/admins → LandingPage). Those pages are built for the legacy
 *         theme, so we re-apply generateTheme (the same factory App.js uses)
 *         over just this body — the RewampLayout chrome above stays rewamp-
 *         themed, mirroring how DefaultLayout pairs rewamp chrome with a
 *         legacy-themed Outlet.
 */
const HomeLanding = ({ isMobile, initData }) => {
  const { fontSize, ourTheme } = useContext(AppLayoutContext);
  const { standalone } = useDisplayMode();
  const roles = getUserRoles() || [];
  const showRewampLanding = roles.some((role) =>
    ROLE_GROUPS.HOME_REWAMP_LANDING.includes(role)
  );

  // Regenerated only when fontSize/ourTheme change — same pattern as the layouts.
  const legacyTheme = useMemo(
    () => generateTheme(fontSize, ourTheme),
    [fontSize, ourTheme]
  );

  if (rewampDashboardAccess()) return <Navigate replace to={`${window.contextPath}/dashboards`} />;

  // The installed app has one home for every non-citizen role.
  if (standalone) return <PwaHomePage isMobile={isMobile} />;
  if (showRewampLanding) return <RewampMainLandingPage isMobile={isMobile} />;

  return (
    <ThemeProvider theme={legacyTheme}>
      <Homepage />
    </ThemeProvider>
  );
};

HomeLanding.propTypes = {
  isMobile: PropTypes.bool,
  initData: PropTypes.object,
};

const FallbackRoute = () => {
  const isLoggedIn = localStorage.getItem("DfsWeb.isLoggedIn") === "true";
  return <Navigate replace to={isLoggedIn ? `${window.contextPath}/home` : loggedOutPath()} />;
};

ApplicationRoutes.propTypes = {
  isMobile: PropTypes.bool,
  initData: PropTypes.object,
};

export default ApplicationRoutes;
